import { useState } from "preact/hooks"
import { useFetch } from "../hooks/useFetch"
import type { ActiveProfile, Topic } from "../types"
import { LevelPicker } from "./LevelPicker"
import { LoadError } from "./LoadError"
import {
	createSoloPlay,
	fetchActiveSoloPlays,
	fetchRecentSoloPlays,
	fetchTopics,
} from "./lib/api"
import { Modal } from "./Modal"
import { TopicIcon } from "./TopicIcons"
import { TopicList } from "./TopicList"

interface KidHomeProps {
	profile: ActiveProfile
	navigate: (path: string) => void
}

// The group name GET /api/topics puts the five-year-olds' topics in. It is
// the string, not a flag, because that is all the API carries — keep it equal
// to topicGroupKids in internal/server/topics.go. The server refuses any
// other topic from a kid session anyway; this is what stops the page offering
// one in the first place.
const kidTopicGroup = "For little kids"

// Home for a kid profile. Deliberately shorter than the owner's Home: no
// matches, no friends, no plan counter, no footer — a profile can reach none
// of that. Big rows, few words, and every link is a link.
export function KidHome({ profile, navigate }: KidHomeProps) {
	// Each fetched section keeps its own error beside its own data, so a
	// section that failed says so and the other two carry on.
	const {
		data: topics,
		error: topicsError,
		reload: loadTopics,
	} = useFetch(
		() =>
			fetchTopics().then((all) => all.filter((t) => t.group === kidTopicGroup)),
		"Couldn't load the games.",
	)
	const {
		data: resume,
		error: resumeError,
		reload: loadResume,
	} = useFetch(fetchActiveSoloPlays, "Couldn't load your rounds.")
	const {
		data: recent,
		error: recentError,
		reload: loadRecent,
	} = useFetch(fetchRecentSoloPlays, "Couldn't load your rounds.")
	// Errors from *starting* a round, including the 402 when the grown-up's
	// free rounds are used up. There is no Pro modal here on purpose: a child
	// cannot buy anything, so the server's own sentence is the whole message.
	const [error, setError] = useState("")
	const [activeTopic, setActiveTopic] = useState<Topic | null>(null)
	const [level, setLevel] = useState(1)
	const [starting, setStarting] = useState(false)

	async function play(topic: Topic) {
		setStarting(true)
		setError("")
		try {
			const state = await createSoloPlay(topic.id, level)
			navigate(`/solo/${state.id}`)
		} catch (e) {
			// Close the dialog so the alert at the top of the page is actually
			// visible — a message behind a modal is a message nobody reads.
			setActiveTopic(null)
			setError(e instanceof Error ? e.message : "Couldn't start that game.")
			setStarting(false)
		}
	}

	// An unfinished round is worth offering back, but an empty list is not
	// worth a heading — so "nothing to resume" is the *absence* of the block,
	// while loading and failed each say themselves.
	const hideResume = !resumeError && resume !== null && resume.length === 0

	return (
		<div class="overflow-auto h-100">
			<div class="container page" style="max-width: 40rem;">
				<h1>Hi {profile.name}!</h1>

				{error && <div class="alert danger">{error}</div>}

				{!hideResume && (
					<div class="stack gap-3">
						<h2>Keep playing</h2>
						{resumeError ? (
							<LoadError message={resumeError} onRetry={loadResume} />
						) : !resume ? (
							<div class="spinner" role="status" />
						) : (
							<div class="list">
								{resume.map((a) => (
									<a
										href={`/solo/${a.id}`}
										key={a.id}
										class="flex between center gap-2"
									>
										<span class="fw-semibold">{a.topic_name}</span>
										<span class="muted">
											{a.current_question + 1} of {a.total}
										</span>
									</a>
								))}
							</div>
						)}
					</div>
				)}

				<div class="stack gap-3">
					<h2>Pick a game</h2>
					{topicsError ? (
						<LoadError message={topicsError} onRetry={loadTopics} />
					) : !topics ? (
						<div class="spinner" role="status" />
					) : topics.length === 0 ? (
						<p class="muted">No games here yet.</p>
					) : (
						<TopicList
							topics={topics}
							hideGroupHeadings
							disabled={starting}
							onSelect={(t) => {
								setActiveTopic(t)
								setLevel(1)
							}}
						/>
					)}
				</div>

				<div class="stack gap-3">
					<h2>Your recent rounds</h2>
					{recentError ? (
						<LoadError message={recentError} onRetry={loadRecent} />
					) : !recent ? (
						<div class="spinner" role="status" />
					) : recent.length === 0 ? (
						<p class="muted">Nothing yet — pick a game above.</p>
					) : (
						<div class="list">
							{recent.map((s) => (
								<a
									href={`/solo/${s.id}`}
									key={s.id}
									class="flex between center gap-2"
								>
									<span class="fw-semibold">{s.topic_name}</span>
									<span class="muted">{s.score}/10</span>
								</a>
							))}
						</div>
					)}
				</div>
			</div>

			<Modal
				open={activeTopic !== null}
				onClose={() => setActiveTopic(null)}
				title={
					activeTopic && (
						<span class="flex center gap-2">
							<TopicIcon topic={activeTopic.id} size="1.25rem" />
							{activeTopic.name}
						</span>
					)
				}
			>
				{activeTopic && (
					<div class="stack gap-3">
						<p>{activeTopic.description}</p>
						<LevelPicker
							levels={activeTopic.levels}
							level={level}
							onChange={setLevel}
						/>
						{/* autofocus so the modal opens on its one action rather than
						    on the close button. */}
						<button
							type="button"
							autofocus
							class="primary lg"
							disabled={starting}
							onClick={() => play(activeTopic)}
						>
							Play
						</button>
					</div>
				)}
			</Modal>
		</div>
	)
}
